import { DirectionalIcon, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUp20Regular,
  ChevronRight20Regular,
  Folder20Regular,
  FolderOpen20Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { useFolderChildren, useRefreshFolders } from './api';
import { documentErrorMessage, type DocumentRow } from './document-model';
import { useRefreshDocuments, useUpdateDocument } from './documents-api';
import { canWrite, isStepUpRequired, type Folder } from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42"},"where":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Eh141a":"fni485r","Bhrd7zp":"fl43uef"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","mlrwpw":"f94mpck","Bmxbyg5":"f5zp4f"},"row":{"Bns2gvi":"f1c209jx","Brf1p80":"fbhxue7","fsow6f":"fpgzoln"},"name":{"Bh6795r":"fqerorx","Btd35i7":"fjksvth"},"muted":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"f11d4kpn"}});

export interface MoveDocumentsDialogProps {
  spaceId: string;
  /** The selected documents, each with its version for `If-Match`. */
  documents: readonly DocumentRow[];
  /** The folder they are in now. */
  currentFolderId: string;
  onClose: () => void;
  /** After the moves (some may have failed): the ids that moved. */
  onMoved: (ids: readonly string[]) => void;
}

/**
 * Picks the destination folder by browsing the tree level by level (the keyboard way of moving
 * documents), then moves each selected document with its own `If-Match`. Documents cannot sit at
 * the root of the project, so a folder must be opened first.
 */
export function MoveDocumentsDialog({
  spaceId,
  documents,
  currentFolderId,
  onClose,
  onMoved,
}: MoveDocumentsDialogProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const update = useUpdateDocument(spaceId);
  const refreshDocuments = useRefreshDocuments(spaceId);
  const refreshFolders = useRefreshFolders(spaceId);

  // The folders opened from the root down to the browsed location (empty: the root).
  const [path, setPath] = useState<Folder[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const here = path.at(-1) ?? null;
  const children = useFolderChildren(spaceId, here?.id ?? null);
  const choices = children.data?.items ?? [];

  const blocked: string | null =
    here === null
      ? t('moveDocuments.pickFolder')
      : here.id === currentFolderId
        ? t('moveDocuments.alreadyHere')
        : canWrite(here)
          ? null
          : t('moveDocuments.noWrite');

  const move = async () => {
    if (pending || blocked || here === null) return;
    setPending(true);
    setFailure(null);
    const moved: string[] = [];
    let firstError: unknown = null;
    for (const document of documents) {
      try {
        await update.mutateAsync({
          documentId: document.id,
          version: document.version,
          data: { folderId: here.id },
        });
        moved.push(document.id);
      } catch (error) {
        if (isStepUpRequired(error)) {
          setPending(false);
          return;
        }
        firstError ??= error;
      }
    }
    setPending(false);
    if (moved.length > 0 || firstError !== null) {
      // Also after a failure: a stale version means the list is out of date.
      await Promise.all([refreshDocuments(), refreshFolders()]);
    }
    if (moved.length > 0) onMoved(moved);
    if (moved.length === documents.length) {
      notify({
        intent: 'success',
        message: t('moveDocuments.done', { count: moved.length, folder: isolate(here.name) }),
      });
      onClose();
    } else if (moved.length > 0) {
      notify({
        intent: 'warning',
        message: t('moveDocuments.partial', {
          moved: moved.length,
          failed: documents.length - moved.length,
          reason: documentErrorMessage(t, firstError),
        }),
      });
      onClose();
    } else {
      setFailure(documentErrorMessage(t, firstError));
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !pending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('moveDocuments.title', { count: documents.length })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <p className={styles.intro}>{t('moveDocuments.intro')}</p>
              <div className={styles.where}>
                <FolderOpen20Regular aria-hidden="true" />
                <span>
                  {t('move.location', { name: isolate(here ? here.name : t('move.root')) })}
                </span>
                {here ? (
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<ArrowUp20Regular />}
                    onClick={() => {
                      setPath((current) => current.slice(0, -1));
                      setFailure(null);
                    }}
                  >
                    {t('move.up')}
                  </Button>
                ) : null}
              </div>
              {children.isError ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{t('tree.loadFailed')}</MessageBarBody>
                  <MessageBarActions>
                    <Button
                      onClick={() => {
                        void children.refetch();
                      }}
                    >
                      {t('common:actions.retry')}
                    </Button>
                  </MessageBarActions>
                </MessageBar>
              ) : !children.data ? (
                <SkeletonList count={3} />
              ) : choices.length > 0 ? (
                <ul className={styles.list} aria-label={t('move.listLabel')}>
                  {choices.map((choice) => (
                    <li key={choice.id}>
                      <Button
                        appearance="subtle"
                        className={styles.row}
                        icon={<Folder20Regular />}
                        iconPosition="before"
                        aria-label={t('move.open', { name: isolate(choice.name) })}
                        onClick={() => {
                          setPath((current) => [...current, choice]);
                          setFailure(null);
                        }}
                      >
                        <bdi className={styles.name}>{choice.name}</bdi>
                        <DirectionalIcon icon={ChevronRight20Regular} aria-hidden="true" />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.muted}>{t('move.noSubfolders')}</p>
              )}
              {blocked ? <p className={styles.muted}>{blocked}</p> : null}
              {failure ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabledFocusable={pending || blocked !== null}
              onClick={() => {
                void move();
              }}
            >
              {pending ? t('move.moving') : t('move.here')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/move-documents-dialog.griffel.css";